/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useState } from 'react';
import { useAsyncFn, useLocalStorage, useUpdateEffect } from 'react-use';
import { v4 as uuid } from 'uuid';
import { useSetAuthToken } from 'api/authTokenApi';
import { ErrorResponse } from 'api/fetchAPI';
import { useSetSession, useClearSession } from 'context';
import {
  SignInSuccessData,
  MFACodeResponse,
  completeMFA,
  initiateMFA,
  initiatePasswordLogin,
} from './api';

export const usePasswordLogin = ({
  success,
  initialEmail,
  receiveOrderPreviewId,
}: {
  success: (data: SignInSuccessData) => void;
  initialEmail?: string;
  receiveOrderPreviewId?: string;
}): [
  {
    invalidEmail: boolean;
    login: () => Promise<SignInSuccessData>;
    loading: boolean;
    error?: ErrorResponse;
    email: string;
    setEmail: (v: string) => void;
    password: string;
    setPassword: (v: string) => void;
    rememberMe: boolean;
    setRememberMe: (v: boolean) => void;
  },
  {
    requiresMFA: boolean;
    usesAuthenticator: boolean;
    initiateMFALoading: boolean;
    verifyLoading: boolean;
    otp: string[];
    setOtp: (v: string[]) => void;
    invalidOtp: boolean;
    sendCode: () => Promise<MFACodeResponse | undefined>;
    debugMfaCode?: MFACodeResponse;
    initiateMFAError?: ErrorResponse;
    verifyCode: () => Promise<SignInSuccessData>;
    phoneNumber?: string;
    verifyError?: ErrorResponse;
  }
] => {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [deviceId] = useLocalStorage('qb_web_deviceId', uuid());
  const [otp, setOtp] = useState<Array<string>>(new Array(6).fill(''));
  const [mfaToken, setMfaToken] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const { mutateAsync: setAuthTokenAsync } = useSetAuthToken();
  const setSession = useSetSession();
  const clearSession = useClearSession();
  const [{ value, loading, error }, login] = useAsyncFn(async () => {
    await clearSession();
    await setAuthTokenAsync(null);
    const authResponse = await initiatePasswordLogin(
      deviceId!,
      email,
      password,
      rememberMe,
      receiveOrderPreviewId
    );
    if (authResponse.mfaToken) {
      setMfaToken(authResponse.mfaToken);
    } else {
      await setSession(
        {
          userId: authResponse.userId,
          firstOnboardingCompleted: authResponse.firstOnboardingCompleted,
          email: authResponse.kyc.email?.address,
          fullName:
            authResponse.kyc.firstName + ' ' + authResponse.kyc.lastName,
        },
        authResponse.tokenTtlInSeconds
      );
      await setAuthTokenAsync(authResponse.token);
      success(authResponse);
    }
    return authResponse;
  }, [success, deviceId, email, password, rememberMe, receiveOrderPreviewId]);

  const [{ loading: verifyLoading, error: verifyError }, verifyCode] =
    useAsyncFn(async () => {
      const completeResponse = await completeMFA(
        otp.join(''),
        mfaToken,
        deviceId!
      );
      const {
        firstOnboardingCompleted,
        kyc: { email, firstName, lastName },
        tokenTtlInSeconds,
      } = completeResponse;
      await setSession(
        {
          userId: completeResponse.userId,
          firstOnboardingCompleted,
          email: email?.address,
          fullName: firstName + ' ' + lastName,
        },
        tokenTtlInSeconds
      );
      await setAuthTokenAsync(completeResponse.token);
      success(completeResponse);
      return completeResponse;
    }, [mfaToken, otp, deviceId, rememberMe, success]);

  const [
    {
      loading: initiateMFALoading,
      value: debugMfaCode,
      error: initiateMFAError,
    },
    sendCode,
  ] = useAsyncFn(() => {
    if (value?.methods?.some((method) => method.type === 'AUTHENTICATOR_APP')) {
      return Promise.resolve(undefined);
    }
    return initiateMFA(mfaToken);
  }, [mfaToken, value]);

  const invalidEmail = useMemo(() => !/(.+)@(.+)\.(.+)$/.test(email), [email]);

  const invalidOtp = useMemo(
    () => otp.join('').length > 0 && otp.join('').length !== 6,
    [otp]
  );

  useUpdateEffect(() => {
    if (mfaToken) void sendCode();
  }, [mfaToken]);

  return [
    {
      invalidEmail,
      login,
      loading,
      error,
      email,
      setEmail,
      password,
      setPassword,
      rememberMe,
      setRememberMe,
    },
    {
      requiresMFA: mfaToken !== '',
      usesAuthenticator: !!value?.methods?.some(
        (method) => method.type === 'AUTHENTICATOR_APP'
      ),
      initiateMFALoading,
      verifyLoading,
      otp,
      setOtp,
      invalidOtp,
      sendCode,
      debugMfaCode,
      initiateMFAError,
      verifyCode,
      verifyError,
      phoneNumber:
        (value?.methods &&
          value.methods.find((e) => e.type === 'PHONE')?.phoneNumber) ||
        undefined,
    },
  ];
};
